import type { AdminVehicleCatalogAliasDto } from '@carcheck/shared';
import AddIcon from '@mui/icons-material/Add';
import CancelIcon from '@mui/icons-material/Cancel';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { AliasAddForm } from './AliasAddForm';
import { deleteCatalogAlias } from '../../../api/admin-vehicle-catalog';
import { ConfirmationDialog } from '../../../components/feedback';
import type { UseCatalogWriteResult } from '../_lib/useCatalogWrite';

/**
 * Aliases of a make (or of one model) (AUT-437): chips «name · kind» with a
 * delete that goes through a confirmation, plus the inline add form. Both
 * writes bump the owner's `updatedAt` without returning it — the runner
 * refetches the make before the next PATCH can go.
 */
export interface AliasSectionProps {
  title: string;
  aliases: readonly AdminVehicleCatalogAliasDto[];
  makeId: string;
  modelId?: string;
  write: UseCatalogWriteResult;
  disabled: boolean;
  onOpenMake: (makeId: string) => void;
}

const AliasLabel = ({ alias }: { alias: AdminVehicleCatalogAliasDto }): ReactElement => {
  const { t } = useTranslation();
  return (
    <span>
      {alias.alias}{' '}
      <Typography component="span" variant="caption" color="text.secondary">
        {t(`vehicleCatalog.aliasKind.${alias.kind}`)}
      </Typography>
    </span>
  );
};

export const AliasSection = (p: AliasSectionProps): ReactElement => {
  const { t } = useTranslation();
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<AdminVehicleCatalogAliasDto | null>(null);

  const remove = async (): Promise<void> => {
    if (!removing) return;
    const { id } = removing;
    await p.write.run(() => deleteCatalogAlias(id), {
      successKey: 'vehicleCatalog.aliases.removed',
    });
    setRemoving(null);
  };

  return (
    <Box component="section">
      <Typography variant="overline" color="text.secondary">
        {p.title} · {p.aliases.length}
        <Typography component="span" variant="caption" sx={{ ml: 1, textTransform: 'none' }}>
          {t('vehicleCatalog.aliases.hint')}
        </Typography>
      </Typography>
      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center', mt: 0.5 }}>
        {p.aliases.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('vehicleCatalog.aliases.none')}
          </Typography>
        ) : null}
        {p.aliases.map((a) => (
          <Chip
            key={a.id}
            variant="outlined"
            label={<AliasLabel alias={a} />}
            onDelete={p.disabled ? undefined : () => setRemoving(a)}
            deleteIcon={
              <CancelIcon titleAccess={t('vehicleCatalog.aliases.remove', { alias: a.alias })} />
            }
          />
        ))}
        {!adding ? (
          <Button
            size="small"
            startIcon={<AddIcon />}
            disabled={p.disabled}
            onClick={() => setAdding(true)}
          >
            {t('vehicleCatalog.aliases.add')}
          </Button>
        ) : null}
      </Box>
      {adding ? (
        <AliasAddForm
          makeId={p.makeId}
          {...(p.modelId ? { modelId: p.modelId } : {})}
          write={p.write}
          onOpenMake={p.onOpenMake}
          onDone={() => setAdding(false)}
        />
      ) : null}
      <ConfirmationDialog
        open={removing !== null}
        variant="destructive"
        title={t('vehicleCatalog.aliases.confirmTitle', { alias: removing?.alias ?? '' })}
        description={t('vehicleCatalog.aliases.confirmBody')}
        confirmLabel={t('vehicleCatalog.aliases.confirm')}
        loading={p.write.busy}
        onConfirm={() => void remove()}
        onCancel={() => setRemoving(null)}
      />
    </Box>
  );
};
